import { useSyncExternalStore, useEffect } from "react";
import { formatEther } from "ethers";
import { getContracts, getProvider } from "@/lib/contracts";
import { createStore } from "@/lib/externalStore";

export interface GlobalStats {
  totalMinted: number;
  totalSupply: number;
  maxSupply: number;
  burned: number;
  activated: number;
  mintPriceWei: bigint;
  mintPriceEth: string;
  mintOpen: boolean;
  currentRound: number;
  totalWeight: bigint;
  potBuffer: bigint;
  potBufferEth: string;
}

const DEFAULT_STATS: GlobalStats = {
  totalMinted: 0,
  totalSupply: 0,
  maxSupply: 1024,
  burned: 0,
  activated: 0,
  mintPriceWei: 0n,
  mintPriceEth: "0",
  mintOpen: false,
  currentRound: 0,
  totalWeight: 0n,
  potBuffer: 0n,
  potBufferEth: "0",
};

/**
 * 全局统计采用「共享 store + 手动刷新」，不做定时轮询。
 *
 * HeroSection / HeroDashboard / MintController 三处都要用这份数据，
 * 各自轮询会造成三倍 RPC 消耗并触发网关 429。
 * 数据只在首次挂载与交易完成后拉取，其余情况由用户自行刷新页面。
 */
const store = createStore<{ stats: GlobalStats; isLoading: boolean }>({
  stats: DEFAULT_STATS,
  isLoading: true,
});

/** 不变量缓存：MAX_SUPPLY 是 constant，mintPriceWei 仅 owner 可改且 renounce 后永久固定 */
let constantsCache: { maxSupply: number; mintPriceWei: bigint } | null = null;

async function loadConstants(nft: ReturnType<typeof getContracts>["nft"]) {
  if (constantsCache) return constantsCache;
  const [maxSupply, mintPriceWei] = await Promise.all([
    nft.MAX_SUPPLY().then((r: bigint) => Number(r)),
    nft.mintPriceWei().then((r: bigint) => r),
  ]);
  constantsCache = { maxSupply, mintPriceWei };
  return constantsCache;
}

/**
 * Activated 事件计数：增量扫描。
 * 首次做一次全量扫描，之后只扫新增区块，避免重复扫描整条链。
 */
const activatedCache = { count: 0, lastBlock: -1 };

async function countActivated(
  nft: ReturnType<typeof getContracts>["nft"],
  latestBlock: number
): Promise<number> {
  const fromBlock = activatedCache.lastBlock + 1;
  if (fromBlock > latestBlock) return activatedCache.count;

  const logs = await nft.queryFilter(nft.filters.Activated(), fromBlock, latestBlock);
  activatedCache.count += logs.length;
  activatedCache.lastBlock = latestBlock;
  return activatedCache.count;
}

let hasLoaded = false;
let inflight: Promise<void> | null = null;

/** 主动刷新全局统计，所有订阅组件同步更新。交易成功后应调用。 */
export async function refreshGlobalStats(): Promise<void> {
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const provider = getProvider();
      const { nft, rounds } = getContracts(provider);

      // 全部走同一个 provider 单例，会被 ethers 合并成一个 JSON-RPC batch
      const [constants, latestBlock, totalMinted, totalSupply, mintOpen, currentRound, totalWeight, potBuffer] =
        await Promise.all([
          loadConstants(nft),
          provider.getBlockNumber(),
          nft.totalMinted().then((r: bigint) => Number(r)),
          nft.totalSupply().then((r: bigint) => Number(r)),
          nft.mintOpen().then((r: boolean) => r),
          rounds.currentRound().then((r: bigint) => Number(r)),
          rounds.totalWeight().then((r: bigint) => r),
          rounds.potBuffer().then((r: bigint) => r),
        ]);

      const activated = await countActivated(nft, latestBlock);

      hasLoaded = true;
      store.set({
        isLoading: false,
        stats: {
          totalMinted,
          totalSupply,
          maxSupply: constants.maxSupply,
          burned: totalMinted - totalSupply,
          activated,
          mintPriceWei: constants.mintPriceWei,
          mintPriceEth: formatEther(constants.mintPriceWei),
          mintOpen,
          currentRound,
          totalWeight,
          potBuffer,
          potBufferEth: formatEther(potBuffer),
        },
      });
    } catch (err) {
      console.error("[useGlobalStats] 读取全局统计失败", err);
      // 出错时保留上一次的数据，不要闪回 0
      store.set({ stats: store.get().stats, isLoading: false });
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}

export function useGlobalStats() {
  const state = useSyncExternalStore(store.subscribe, store.get);

  // 仅首次拉取一次；后续由交易完成或用户刷新页面触发
  useEffect(() => {
    if (!hasLoaded && !inflight) refreshGlobalStats();
  }, []);

  return { stats: state.stats, isLoading: state.isLoading };
}
